import { useCurrentUser } from './use-current-user';
import { useCurrentOrganization } from './use-current-organization';
import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { Permission, Role, ROLE_PERMISSIONS } from '@mangelapp/shared';

// Re-export types for backward compatibility
export type { Permission, Role };

interface UsePermissionsOptions {
  organizationId?: string | null;
}

export function usePermissions(options?: UsePermissionsOptions) {
  const { user } = useCurrentUser();
  
  // Use provided organizationId or fall back to user's organizationId
  const orgId = options?.organizationId ?? ((user as any)?.organizationId || null);
  
  // Fetch user data with organization context if organizationId is provided
  const { data: meResponse } = useQuery({
    queryKey: ['auth', 'me', orgId],
    queryFn: async () => {
      const url = orgId 
        ? `/api/auth/me?organizationId=${encodeURIComponent(orgId)}`
        : '/api/auth/me';
      return apiRequest(url).then(res => res.json());
    },
    enabled: !!options?.organizationId && !!orgId, // Only fetch if organizationId is explicitly provided
    retry: false,
    staleTime: 0,
  });
  
  // Use user from meResponse if available (for specific org context), otherwise use default user
  const contextUser = (options?.organizationId && (meResponse as any)?.user) ? (meResponse as any)?.user : user;
  
  // Debug logging
  // console.log('🔍 usePermissions - User (nested):', contextUser);
  // console.log('🔍 usePermissions - User orgRole:', contextUser?.orgRole);
  // console.log('🔍 usePermissions - User role:', contextUser?.role);
  // console.log('🔍 usePermissions - OrgId:', orgId);
  
  // Resolve user's role from org-scoped channels only; never use global users.role
  const resolvedRole = (contextUser as any)?.resolvedOrgRole ?? (contextUser as any)?.orgRole ?? null;
  const userRole: Role = (resolvedRole as Role) || 'contractor';

  // Try to load org-defined permissions for this role
  const { data: rolesResponse, isLoading: rolesLoading } = useQuery({
    queryKey: ['/api/orgs', orgId, 'roles'],
    queryFn: async () => {
      const res = await apiRequest(`/api/orgs/${orgId}/roles`);
      return await res.json();
    },
    enabled: !!orgId,
    staleTime: 60_000,
    retry: false,
  });
  const orgRoles = (rolesResponse as any)?.data || [];
  const orgRole = orgRoles.find((r: any) =>
    String(r.name || '').toLowerCase() === String(userRole || '').toLowerCase()
  );
  const orgRolePermissions: Permission[] | null = orgRole
    ? (orgRole.permissions || []).filter((p: any) => p.allowed).map((p: any) => p.permission)
    : null;
  const { data: effectivePermissionsResponse, isLoading: effectivePermissionsLoading } = useQuery({
    queryKey: ['/api/orgs/effective-permissions', orgId],
    queryFn: async () => {
      const url = `/api/orgs/effective-permissions?organizationId=${encodeURIComponent(orgId as string)}`;
      const res = await apiRequest(url);
      return await res.json();
    },
    enabled: !!orgId,
    staleTime: 60_000,
    retry: false,
  });
  const rawEffective = (effectivePermissionsResponse as any)?.data?.allowedPermissions;
  const effectivePermissions: Permission[] | null = Array.isArray(rawEffective)
    ? rawEffective
    : null;

  const hasPermission = (permission: Permission): boolean => {
    if (effectivePermissions) {
      if (effectivePermissions.includes(permission)) return true;
      // Owner always has all permissions; if API returned incomplete list, grant from default owner role
      if (userRole === 'owner') {
        const ownerPerms = ROLE_PERMISSIONS.owner || [];
        return ownerPerms.includes(permission);
      }
      return false;
    }
    const rolePermissions = orgRolePermissions || ROLE_PERMISSIONS[userRole] || [];
    return rolePermissions.includes(permission);
  };

  const hasAnyPermission = (permissions: Permission[]): boolean => {
    return permissions.some(permission => hasPermission(permission));
  };

  const hasAllPermissions = (permissions: Permission[]): boolean => {
    return permissions.every(permission => hasPermission(permission));
  };

  const canManageOrganization = () => hasPermission('manage_organization');
  const canManageMembers = () => hasPermission('manage_members');
  const canManageAssignedProjects = () => hasPermission('manage_projects');
  const canManageAllProjects = () => hasPermission('manage_all_projects');
  const canViewAssignedProjects = () => hasPermission('view_projects');
  const canViewAllProjects = () => hasPermission('view_all_projects');
  const canManageProjects = () => canManageAssignedProjects() || canManageAllProjects();
  const canViewProjects = () => canViewAssignedProjects() || canViewAllProjects() || canManageProjects();
  const canManageTickets = () => canManageProjects();
  const canCreateTickets = () => canManageProjects();
  const canEditTickets = () => canManageProjects();
  const canAddPhotosToTickets = () => canManageProjects();
  const canMessageInTickets = () => canManageProjects();
  const canManageLists = () => hasPermission('manage_lists');
  const canViewReports = () => hasPermission('view_reports');
  const canUploadDocuments = () => canManageProjects() || canManageLists();
  const canManageAIFeatures = () => hasPermission('ai_image_analysis');
  // Check if user is owner (using role check as fallback)
  const isOwner = userRole === 'owner';
  const canConfirmOvertime = () => hasPermission('confirm_overtime') || isOwner;
  const canDeleteTickets = () => canManageProjects() || isOwner;
  const canExportData = () => hasPermission('export_data') || isOwner;
  const canManageProjectSettings = () => canManageProjects() || isOwner;
  const permissionsLoading = !!orgId && (rolesLoading || effectivePermissionsLoading);

  return {
    userRole,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canManageOrganization,
    canManageMembers,
    canManageProjects,
    canManageAssignedProjects,
    canManageAllProjects,
    canViewProjects,
    canViewAssignedProjects,
    canViewAllProjects,
    canManageTickets,
    canCreateTickets,
    canEditTickets,
    canAddPhotosToTickets,
    canMessageInTickets,
    canManageLists,
    canViewReports,
    canUploadDocuments,
    canManageAIFeatures,
    canConfirmOvertime,
    canDeleteTickets,
    canExportData,
    canManageProjectSettings,
    permissionsLoading,
  };
}

/**
 * Hook to check if the current user is an organization owner
 * @param organizationId - Optional organization ID to check ownership for (defaults to current organization)
 * @returns Object with isOwner boolean and isLoading state
 */
export function useIsOrganizationOwner(organizationId?: string | null) {
  const { user } = useCurrentUser();
  const { organizationId: currentOrgId } = useCurrentOrganization();
  const orgId = organizationId ?? currentOrgId;

  const { data: ownershipResponse, isLoading } = useQuery({
    queryKey: ['/api/orgs/check-ownership', orgId],
    queryFn: async () => {
      const url = orgId 
        ? `/api/orgs/check-ownership?organizationId=${encodeURIComponent(orgId)}`
        : '/api/orgs/check-ownership';
      const res = await apiRequest(url);
      return await res.json();
    },
    enabled: !!orgId,
    staleTime: 60_000,
    retry: false,
  });

  // Also check if userRole is 'owner' as a fallback
  const { userRole } = usePermissions({ organizationId: orgId });
  const isOwnerFromRole = userRole === 'owner';

  const isOwner = (ownershipResponse as any)?.ownsOrganization ?? isOwnerFromRole;

  return {
    isOwner,
    isLoading,
  };
}
